import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { PLATFORM_ID, TransferState, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  RedirectCommand,
  ResolveFn,
  Router,
  UrlMatcher,
  UrlSegment,
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isProtectedRoot } from '@blauwtand/seo-hierarchy';
import { SeoPageService } from '../services/seo-page.service';
import { SeoResolution, SeoRouteState } from './seo-page.models';
import { SEO_PAGE_RENDER_PAYLOAD, SEO_PAGE_TRANSFER_KEY } from './seo-render-payload';

/**
 * Matches every path outside the fixed application namespaces. Declared after all fixed routes
 * and before `**`; whether the path is a page, an alias or missing is decided only by the
 * full-path resolve API, never by a client-side slug catalogue.
 */
export const seoPathMatcher: UrlMatcher = (segments: UrlSegment[]) => {
  if (segments.length === 0 || isProtectedRoot(segments[0].path)) return null;
  const path = `/${segments.map((segment) => segment.path).join('/')}`;
  return { consumed: segments, posParams: { seoPath: new UrlSegment(path, {}) } };
};

export function seoRoutePath(route: ActivatedRouteSnapshot): string {
  return `/${route.url.map((segment) => segment.path).join('/')}`;
}

/** Target URL tree for a resolved alias, keeping the original query and fragment. */
export function redirectTarget(router: Router, route: ActivatedRouteSnapshot, path: string) {
  const target = router.parseUrl(path);
  target.queryParams = { ...route.queryParams };
  target.fragment = route.fragment;
  return target;
}

/**
 * Resolves the SEO route once per navigation. A new navigation unsubscribes the previous
 * resolution, so a late response for an abandoned path can never render. Aliases redirect to
 * the resolved path; missing and unavailable outcomes render explicit states.
 */
export const seoRouteResolver: ResolveFn<SeoRouteState> = (route) => {
  const path = seoRoutePath(route);
  const seeded = consumeRenderPayload(path);
  if (seeded) return of(seeded);

  const router = inject(Router);
  const toOutcome = (resolution: SeoResolution): SeoRouteState | RedirectCommand => {
    if (resolution?.kind === 'page' && resolution.page) {
      return { kind: 'page', path, revision: resolution.revision, page: resolution.page };
    }
    if (resolution?.kind === 'redirect' && typeof resolution.path === 'string' && resolution.path.startsWith('/') && resolution.path !== path) {
      // No explicit history option: the router replaces the entry for browser-entered or
      // popstate URLs and pushes only the target for in-app links, so an alias never gets its
      // own history entry and the previous page's entry is kept.
      return new RedirectCommand(redirectTarget(router, route, resolution.path));
    }
    return { kind: 'unavailable', path };
  };
  const failed = (error: HttpErrorResponse): Observable<SeoRouteState> =>
    of(error?.status === 404 || error?.status === 400 ? { kind: 'not-found', path } : { kind: 'unavailable', path });
  const outcome$: Observable<SeoRouteState | RedirectCommand> = inject(SeoPageService).resolve(path).pipe(map(toOutcome), catchError(failed));
  return outcome$;
};

/**
 * Server: the renderer's request-scoped payload for exactly this path, recorded in TransferState.
 * Browser: the transferred payload is consumed and removed once, during hydration of that path.
 */
function consumeRenderPayload(path: string): SeoRouteState | null {
  const transferState = inject(TransferState);
  const seed = inject(SEO_PAGE_RENDER_PAYLOAD, { optional: true });
  if (seed && seed.path === path) {
    transferState.set(SEO_PAGE_TRANSFER_KEY, seed);
    return { kind: 'page', path, revision: seed.revision, page: seed.page };
  }
  if (isPlatformBrowser(inject(PLATFORM_ID)) && transferState.hasKey(SEO_PAGE_TRANSFER_KEY)) {
    const transferred = transferState.get(SEO_PAGE_TRANSFER_KEY, null);
    transferState.remove(SEO_PAGE_TRANSFER_KEY);
    if (transferred?.path === path) {
      return { kind: 'page', path, revision: transferred.revision, page: transferred.page };
    }
  }
  return null;
}
